import { useState } from 'react'
import { useNavigate, Navigate, Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { useLoginMutation, useLazyMeQuery } from '@/features/auth/auth-api'
import { setCredentials, setIdentity } from '@/features/auth/auth-slice'
import { api } from '@/app/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Logo } from '@/components/layout/logo'
import { useI18n } from '@/lib/i18n'
import type { ApiError } from '@/lib/types'

export function LoginPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated)
  const { t } = useI18n()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(true)
  const [login, { isLoading }] = useLoginMutation()
  const [fetchMe] = useLazyMeQuery()

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    try {
      const res = await login({ email, password }).unwrap()
      dispatch(setCredentials({ access_token: res.access_token, remember }))
      dispatch(api.util.resetApiState())
      const me = await fetchMe().unwrap()
      dispatch(setIdentity({ user: me.user, company: me.company }))
      toast.success(`Welcome back, ${me.user.full_name}`)
      navigate('/', { replace: true })
    } catch (err) {
      const apiErr = (err as { data?: ApiError })?.data
      toast.error(apiErr?.message ?? 'Login failed. Check your credentials.')
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-ink p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Logo className="[&_span]:text-brand-cream" />
        </div>
        <div className="rounded-lg border border-border bg-card p-8 shadow-lg">
          <h1 className="font-display text-2xl font-semibold">{t('auth.sign_in')}</h1>
          <p className="mb-6 mt-1 text-sm text-muted-foreground">
            Access your DistroOps admin portal.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground select-none">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded border-input accent-brand-terracotta"
              />
              {t('auth.remember_me')}
            </label>
            <Button type="submit" className="w-full" variant="brand" disabled={isLoading}>
              {isLoading ? t('common.loading') : t('auth.sign_in')}
            </Button>
          </form>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link to="/signup" className="font-medium text-brand-ink hover:underline">
              Start a 14-day free trial
            </Link>
          </p>
        </div>
        <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-wider text-brand-cream/50">
          DistroOps · Admin Portal v0.1
        </p>
      </div>
    </div>
  )
}
